import React, { ReactElement, useEffect, useMemo, useCallback, useState } from "react";
import {
    QualityHubTaskListPanel,
    QualityHubTasksList,
    TaskListPanelGroup,
    TaskListPanelItem,
    QualityHubTaskFormDrawer,
} from "src/components";
import { useParams, Redirect, useLocation } from "react-router";
import { QualityHubDayViewRouteParams, QualityHubDayViewStateProps, QualityHubDayViewState } from "./QualityHubDayView.types";
import { useDispatch, useSelector } from "react-redux";
import { queryMyTaskLists, canSeeListOverviewBar } from "src/redux";
import SpintrLoader from "src/ui/components/Loader";
import { SpintrTypes } from "src/typings";
import { dateToDaysOfWeekEnum } from "src/utils";
import { localize } from "src/l10n";
import { Conditional } from "src/components/Conditional";
import { ConditionalRender } from "src/components/ConditionalRender";
import { QualityHubListsOverviewBar } from "src/components/QualityHubListsOverviewBar";

/**
 * Renders the 'My Day' view of the Quality Hub.
 *
 * @returns {ReactElement} The rendered component
 */
function QualityHubDayView(): ReactElement {
    const [state, setState] = useState<QualityHubDayViewState>({
        formDrawerOpen: false,
    });

    const location = useLocation();
    const listMode = useMemo<Spintr.QualityHubTasksListMode>(() => {
        if (location.pathname.includes("/customize")) {
            return SpintrTypes.QualityHubTasksListMode.Customization;
        }

        if (location.pathname.includes("/manage")) {
            return SpintrTypes.QualityHubTasksListMode.Creation;
        }

        return SpintrTypes.QualityHubTasksListMode.Execution;
    }, [location.pathname]);

    const { listId } = useParams<QualityHubDayViewRouteParams>();
    const dispatch = useDispatch();
    const {
        displayOverviewBar,
        hasQueried,
        isLoading,
        smallViewMode,
        taskLists,
    } = useSelector<Spintr.AppState, QualityHubDayViewStateProps>((appState) => ({
        displayOverviewBar: canSeeListOverviewBar(appState),
        fetchedForUberId: appState.qualityHub.listsFetchedForUberId,
        hasQueried: appState.qualityHub.myLists.hasQueried,
        isLoading: appState.qualityHub.myLists.loading,
        smallViewMode: appState.ui.isSmallViewMode,
        taskLists: appState.qualityHub.myTaskListsIds.map(
            (id) => appState.qualityHub.listsById[id],
        ),
    }));

    useEffect(function _QualityHubDayViewMount() {
        dispatch(queryMyTaskLists(state.selectedNodeId));
    }, [dispatch, state.selectedNodeId]);

    const groups = useMemo(() => {
        const lists = taskLists
            .filter((list) => (list.tasks ?? []).length > 0
                           || list.listType === SpintrTypes.TaskListType.ActionList)
            .map((list) => {
                const dayOfWeek = dateToDaysOfWeekEnum(new Date());

                const tasks = (list.tasks ?? []).filter(
                    (task) => (!task.daysOfWeek
                           || (task.daysOfWeek & dayOfWeek) === dayOfWeek)
                           && (task.contentStatus === SpintrTypes.ContentStatus.Published),
                );

                const completedCount = (list.statuses ?? [])
                    .filter(
                        (status) => tasks.some((t) => t.id === status.itemId) && 
                            (status.status === SpintrTypes.TaskListItemStatus.Completed
                            || status.status === SpintrTypes.TaskListItemStatus.Failed),
                    )
                    .reduce(
                        (acc, status) => acc.every((id) => id !== status.itemId)
                            ? [...acc, status.itemId]
                            : acc,
                    []).length;

                return {
                    id: list.id,
                    key: list.id,
                    label: list.name,
                    taskCount: tasks.length,
                    completedCount: Math.min(tasks.length, completedCount),
                    canAddTasks: list.listType === SpintrTypes.TaskListType.ActionList,
                    allowOverride: list.allowOverride && list.listType !== SpintrTypes.TaskListType.ActionList,
                }
            });

        const groups: TaskListPanelGroup[] = [];

        const inProgress = lists
            .filter((list) => list.completedCount < list.taskCount);

        if (inProgress.length > 0) {
            groups.push({
                key: "in-progress",
                label: localize("Pagaende"),
                items: inProgress,
            });
        }

        const completed = lists
            .filter((list) => list.completedCount >= list.taskCount);

        if (completed.length > 0) {
            groups.push({
                key: "completed",
                label: localize("Klar"),
                items: completed,
            });
        }

        return groups;
    }, [taskLists]);

    const onAddClicked = useCallback(
        (item: TaskListPanelItem) => {
            const taskList = taskLists.find((list) => list.id === item.id);
            if (!taskList) {
                console.log("Task list not found for item:", item, taskLists);
                return;
            }

            setState((state) => ({
                ...state,
                formDrawerOpen: true,
                selectedList: taskLists.find((list) => list.id === item.id),
            }));
        }, [taskLists]);

    const onDrawerDismiss = useCallback(() => setState((state) => ({
        ...state,
        formDrawerOpen: false,
        selectedList: undefined,
    })), []);

    const selectedId = useMemo(() => parseInt(listId, 10), [listId]);

    const onNodeChanged = useCallback(
        (nodeId: number | undefined) => setState((state) => ({
            ...state,
            selectedList: undefined,
            formDrawerOpen: false,
            selectedNodeId: nodeId,
        })), [setState]);


    if (!hasQueried || isLoading) {
        return <SpintrLoader />;
    }

    if (isNaN(selectedId)) {
        const taskList = taskLists
            .find((list) => list.tasks.length > 0);

        if (!taskList) {
            return null;
        }

        if (!smallViewMode) {
            return <Redirect to={`/quality-hub/${taskList.id}`} />;
        }
    }

    const isExecutionMode = listMode === SpintrTypes.QualityHubTasksListMode.Execution;

    return (
        <div id="QualityHubDayView">
            <ConditionalRender condition={displayOverviewBar && isExecutionMode && !smallViewMode}>
                {() => (
                    <QualityHubListsOverviewBar
                        onNodeChanged={onNodeChanged} />
                )}
            </ConditionalRender>
            <div className="QualityHubDayView-content">
                <Conditional condition={!smallViewMode || !selectedId}>
                    <div className="QualityHubDayView-sidebar">
                        <QualityHubTaskListPanel groups={groups}
                                                 onAddClick={onAddClicked} />
                    </div>
                </Conditional>
                <ConditionalRender condition={!!selectedId}>
                    {() => (
                        <div className="QualityHubDayView-content">
                            <QualityHubTasksList listId={selectedId}
                                                 listMode={listMode} />
                        </div>
                    )}
                </ConditionalRender>
            </div>
            <QualityHubTaskFormDrawer listId={state.selectedList?.id ?? 0}
                                      onDismiss={onDrawerDismiss}
                                      open={state.formDrawerOpen} />
        </div>
    );
}

export default QualityHubDayView;

